<template>
  <form id="payment-form">
    <div id="payment-element"></div>
  </form>
</template>

<script>
import { loadStripe } from "@stripe/stripe-js";
import { getAppCustomEnv } from "@/utilities/get-app-custom-env";
import PaymentServiceProxy from "@/proxies/back-end/subscriptions/payment.microservice";

let stripe;
let elements;
let paymentElement;
let defaultMaxRetry = 5;

const init = async (component) => {
  try {
    let appCustomEnv = await getAppCustomEnv();
    stripe = await loadStripe(appCustomEnv.VUE_APP_STRIPE_PUBLISHABLE_KEY);

    component.payment = await updateOrCreatePaymentIntent(component);
    component.paymentIntentId = component.payment.paymentIntentId;
    component.customerId = component.payment.customerId;
    component.$emit("onLoadPaymentIntentDone");

    elements = stripe.elements({ clientSecret: component.payment.clientSecret });
    paymentElement = elements.create('payment', { layout: { type: component.layoutType ?? 'accordion', spacedAccordionItems: true, defaultCollapsed: false } });
    paymentElement.mount("#payment-element");

    paymentElement.on('change', function (event) {
      if (!event.collapsed) {
        component.$emit("onFullFillPayment", event.complete);

        const newPaymentMethod = event.value.type;
        if (component.paymentMethod && component.paymentMethod !== newPaymentMethod) {
          component.$emit("onPaymentMethodChange", newPaymentMethod);
        }
        component.paymentMethod = newPaymentMethod;
      }
    });
  } catch (error) {
    if (error?.message) {
      error.actualMessage = error.message;
      error.message = undefined;
    }
    component.$emit("onStripePaymentError", error );
  }
}

const updateOrCreatePaymentIntent = async (component, retired = 1) => {
  const maxRetry = component.maxRetry || defaultMaxRetry;

  try {
    const paymentProxy = new PaymentServiceProxy();

    const paymentIntentPayload = {
      companyId: component.companyId,
      companyName: component.companyName,
      paymentType: component.paymentType || "onboarding",
      items: component.services,
      paymentMethodTypes: component.paymentMethodTypes,
      couponCode: component.couponCode,
      userId: component.userId,
    };
    if (component.referenceId) {
      paymentIntentPayload.referenceId = component.referenceId;
    }

    const paymentIntent = await paymentProxy.createPaymentIntent(paymentIntentPayload);
    
    return paymentIntent;
  } catch (error) {
    retired++;

    if (retired <= maxRetry) {
      console.log('Stripe Payment Form - Retired: ', retired)
      return await updateOrCreatePaymentIntent(component, retired);
    }

    if (error?.message) {
      error.actualMessage = error.message;
      error.message = undefined;
    }
  }
}

export default {
  async created() {
    await init(this);
  },
  methods: {
    onCloseAllPaymentMethods: function () {
      console.log("onCloseAllPaymentMethods");
      paymentElement.collapse();
    },
    confirmPayment: async function () {
      try {
        const confirmResponse = await stripe.confirmPayment({
          elements,
          redirect: "if_required",
        });
        if (confirmResponse.error) {
          throw new Error(confirmResponse.error.message);
        }
        return confirmResponse;
      } catch (error) {
        console.error('Confirm payment intent error: ', error);
        throw error;
      }
    },
    refresh: async function() {
      this.payment = await updateOrCreatePaymentIntent(this);

      // Fetch updates on payment intent
      await elements.fetchUpdates();
    }
  },
  props: {
    companyId: String,
    companyName: String,
    couponCode: String,
    services: Array,
    maxRetry: Number,
    paymentMethodTypes: Array,
    paymentType: String,
    layoutType: String,
    userId: String,
    referenceId: String,
  },
  watch: {
    couponEligible() {
      this.refresh()
    }
  },
  computed: {
    couponEligible() {
      return this.couponCode;
    },
  },
  data: function () {
    return {
      authToken: '',
      paymentIntentId: '',
      customerId: '',
      paymentIntent: null,
      retried: 0,
      paymentMethod: null,
    }
  }
};
</script>
